import { action, observable } from 'mobx';
import { appConfig } from 'cd-eft/config/AppConfig';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import * as PROJ from 'cd-eft/services/eftPrjAdminApi';
import { toDecimalPrecision } from 'cd-eft/utils/mathUtilities';
import { WaterwayDesignToolsController, WaterwayDesignToolsReachJsonController } from 'cd-eft/services/eftSetApi';
import { DynamicWaterwayTableStore } from 'cd-eft/models/Stores/DynamicProfile/Waterway/DynamicWaterwayTableStore';
import { DynamicWaterwayHub } from 'cd-eft/models/Stores/DynamicProfile/Waterway/DynamicWaterwayHub';
import { WaterwayInputVM } from './WDTContentComp/WaterwayInput/WaterwayInputVM';
import { convertStationBasedOnWaterwayVertexParamDTO, IWaterwayVertexParamDTO } from 'cd-eft/models/Stores/WaterwayDesignStore';


type WaterwayStep = 'waterway-input' | 'design-waterway' | 'results';

const { designStore, waterwayDesignStore } = stores.AppStore;

export class WDTContainerVM {
  private tableStore: DynamicWaterwayTableStore;
  waterwayInputVM: WaterwayInputVM;
  @observable isCollapsed: boolean = false;
  @observable addWaterwayClicked: boolean = false;
  // @observable triggerReloadInputs: boolean = true;
  history;

  constructor(history) {
    this.history = history;
    this.waterwayInputVM = new WaterwayInputVM();
    const hub = new DynamicWaterwayHub();
    this.tableStore = new DynamicWaterwayTableStore(hub);
  }
  
  @action.bound init = () => {
    designStore.designElementTypeId = appConfig.hardCodes.designElementTypeWaterway //temporarily enable ToolBar on the map for Waterway until the get api functions are implemented
    this.getErrors();
  }
  
  @action.bound atemptSetWorkflowStep(step: WaterwayStep) {
    if (waterwayDesignStore.currentWorkflowStep === 'design-waterway' && (!designStore.allFieldsValids || designStore.wdtGroupsLength > 0)) {
        this.toggleLeavingWarningDialog();
    } 
    else if(waterwayDesignStore.currentWorkflowStep === 'waterway-input' && waterwayDesignStore.waterwayInputsPageInputsChange) {
      this.toggleLeavingWarningDialog();
    }
    else{
      this.setWorkflowStep(step);
    }
  }

  @action.bound setWorkflowStep(step: WaterwayStep) {
    waterwayDesignStore.currentWorkflowStep = step;
    waterwayDesignStore.renderWaterwayDesign = step === 'design-waterway' ? true : false;
  }

  @observable leavingWarningDialogIsOpen: boolean = false;

  @action.bound toggleLeavingWarningDialog = () => {
    this.leavingWarningDialogIsOpen = !this.leavingWarningDialogIsOpen;
  }

  @action.bound toggleCollapsed() {
    this.isCollapsed = !this.isCollapsed;
  }

  
  @action.bound noProjectMessage() {
    MessageCenter.addWarning(designStore.noProjectMessage, [
      {
        callback: () => {
          this.history?.push('/projects/new-project');
          MessageCenter.clearMessages();
        },
        text: 'Go Create a Project',
      },
    ]);
  }

  @action onClickEdit = () => {
    this.addWaterwayClicked = !this.addWaterwayClicked;
    if(this.addWaterwayClicked){
      waterwayDesignStore.requiredInputsValidated = false;
      // this.triggerReloadInputs = true;
    }
    stores.AppStore.waterwayDesignStore.reset();

    if (this.addWaterwayClicked && !designStore.hasProject()) {
      this.noProjectMessage();
    }
  }
  @action.bound onClickAddButtonOnSummaryPage = () => {
    waterwayDesignStore.saveWaterwayTypePage = true;
  }
  @action toggleAddWaterwayClicked = () => {  
    waterwayDesignStore.currentWorkflowStep = 'waterway-input'; //we need this line to avoid flikkering after we edit waterway design that goes back to input page comming from last waterway design that navigated to design page
    designStore.displayResultBanner= false;
    designStore.disableSaveButtonInputPage = true;
    designStore.disableSaveProceedButtonInputPage = true;   
    this.addWaterwayClicked = !this.addWaterwayClicked;
    if(this.addWaterwayClicked){
      waterwayDesignStore.requiredInputsValidated = false;
      // this.triggerReloadInputs = true;
    }
    else if (waterwayDesignStore.showWSCMiniComp) {
      waterwayDesignStore.closeWscComponent();
    }
    // stores.AppStore.waterwayDesignStore.reset();

    if (this.addWaterwayClicked && !designStore.hasProject()) {
      this.noProjectMessage();
    }
  }

  @action.bound saveWaterwayOnSelectWaterwayTypePage = () => {
    waterwayDesignStore.saveWaterwayTypePage = false;
    this.toggleAddWaterwayClicked();
  }

  @action.bound  handleOnClickCloseButtonOnSelectWaterwayTypePage = () => {
    waterwayDesignStore.reset();
    waterwayDesignStore.saveWaterwayTypePage = false;
  }

  get stepToTitle() {
    const baseTitle = (() => {
        switch (waterwayDesignStore.currentWorkflowStep) {
            case 'waterway-input':
                return 'Waterway Inputs';
            case 'design-waterway':
                return 'Waterway Design';
            case 'results':
                return 'Results';
            default:
                return '';
        }
    })();     
    let additionalTitle = '';
    if (waterwayDesignStore.currentWaterwayDesignElementTypeId === 19) {
        additionalTitle = 'Lined Waterway';
    } else if (waterwayDesignStore.currentWaterwayDesignElementTypeId === 7) {
        additionalTitle = 'Grassed Waterway';
    }

    let finalTitle = baseTitle;
    if (!waterwayDesignStore.waterwayType) {
        finalTitle = additionalTitle ? `${baseTitle} - ${additionalTitle}` : baseTitle;
    }

    return waterwayDesignStore.waterwayType ? `${finalTitle} - ${waterwayDesignStore.waterwayType}` : finalTitle;
  
  }

  @action.bound getAllDataOnMount = async () => {
    if (stores.AppStore.designStore.projectsStore.currentSessionId && stores.AppStore.waterwayDesignStore.alignedDesignElementId) {
      designStore.setLoader('Loading...');
      await this.getNotificationStatus();
      if (waterwayDesignStore.aligmentChange < 2){
        await this.getAllReachesByAlignedElementDesignParametersId();
      }
      else if(waterwayDesignStore.aligmentChange === 2){
        waterwayDesignStore.waterwayDesignObject.waterwayDesignStations = [];
      }
      await this.getDesignUiState();
      designStore.setLoader('');
    } else {
      waterwayDesignStore.currentWorkflowStep = 'waterway-input';
    }
    
  }

  @action.bound getNotificationStatus = async () => {
    const paramsId = waterwayDesignStore.alignedDesignElementId
    if(paramsId){
      try{
        const notificationStatus = await WaterwayDesignToolsController.getAllNotificationStatusUsingGET(paramsId);
        if (notificationStatus && notificationStatus.data) {
          const data = notificationStatus.data;
          waterwayDesignStore.aligmentChange = data.alignmentChange ? data.alignmentChange : 0;
          waterwayDesignStore.hydrologyChange = data.hydroChange ? data.hydroChange : 0;
          waterwayDesignStore.designControlChange = data.alignedFeatureSCChange ? data.alignedFeatureSCChange : 0;
      }
      }catch(error){
        console.error('getNotificationStatus error:', error)
      }
    }
  }

  
  @action.bound getDesignUiState = async () => {
    const sessionUUid = stores.AppStore.designStore.projectsStore.currentSessionId;
    const alignmentElementId = stores.AppStore.waterwayDesignStore.alignedDesignElementId;
    try{
        designStore.setLoader('Loading...');
        const response = await PROJ.EftProjUserInputController.getDesignUiStateUsingGET1(sessionUUid, alignmentElementId)
        if (response.data.alignedElementUiStateDto?.designElementTypeId) {
          waterwayDesignStore.currentWaterwayDesignElementTypeId = response.data.alignedElementUiStateDto.designElementTypeId;
        }
        if(response && response.data){
            const responseData = response.data;
          if (responseData.errorCode === 'Success') {
            const uiStateType = responseData.uiStateType;
            waterwayDesignStore.uiState = uiStateType!;
            // workstep:
            if (uiStateType === "WaterwayInputSave") {
              waterwayDesignStore.currentWorkflowStep = "waterway-input"
            } else if (uiStateType === "WaterwayInputProceed" || uiStateType === "ReachDesignCalculate" || uiStateType === "ReachDesignSurface" || uiStateType === "ReachDesignSave") {
              waterwayDesignStore.currentWorkflowStep = "design-waterway"
              waterwayDesignStore.renderWaterwayDesign = true;
            }
            // buttons control:
            if (uiStateType === "WaterwayInputSave") {
              designStore.disableSaveButtonInputPage = true;
              designStore.disableSaveProceedButtonInputPage = false;
              this.buttonsControlDesignPage();
            }
            else if (uiStateType === "WaterwayInputProceed") {
              designStore.disableSaveButtonInputPage = true;
              designStore.disableSaveProceedButtonInputPage = true;
              this.buttonsControlDesignPage();
            }
            else if (uiStateType === "ReachDesignCalculate") {
              designStore.disableSaveButtonInputPage = true;
              designStore.disableSaveProceedButtonInputPage = true;
              waterwayDesignStore.waterwayDesignObject.disableCalculateButtonDesignPage = true;
              waterwayDesignStore.waterwayDesignObject.DisableSaveButtonDesignPage = true;
              waterwayDesignStore.waterwayDesignObject.hasWaterwayCalculations = true;
              designStore.displayResultBanner = true;
            }
            else if (uiStateType === "ReachDesignSave") {
              designStore.disableSaveButtonInputPage = true;
              designStore.disableSaveProceedButtonInputPage = true;
              waterwayDesignStore.waterwayDesignObject.disableCalculateButtonDesignPage = false;
              waterwayDesignStore.waterwayDesignObject.DisableSaveButtonDesignPage = true;
              waterwayDesignStore.waterwayDesignObject.hasWaterwayCalculations = false;
            } else if (uiStateType === "ReachDesignSurface") {
              designStore.disableSaveButtonInputPage = true;
              designStore.disableSaveProceedButtonInputPage = true;
              waterwayDesignStore.waterwayDesignObject.disableCalculateButtonDesignPage = true;
              waterwayDesignStore.waterwayDesignObject.DisableSaveButtonDesignPage = true;
              waterwayDesignStore.waterwayDesignObject.hasWaterwayCalculations = false;
            }
            if (response.data.alignedElementUiStateDto?.uiSettingsDto) {
              const uiSettingsDto = response.data.alignedElementUiStateDto?.uiSettingsDto;
              waterwayDesignStore.profileSettings = {
                bankProfile: uiSettingsDto.bankProfile!,
                fineGrid: uiSettingsDto.fineGrid!,
                lowBankOffset: uiSettingsDto.lowBankOffset!,
                sampleInterval: uiSettingsDto.sampleInterval!,
                showCoord: uiSettingsDto.showCoord!,
                verticalInterval: uiSettingsDto.verticalInterval!
              }
              if (response.data.alignedElementUiStateDto.tableView) {
                waterwayDesignStore.tableView = response.data.alignedElementUiStateDto.tableView;
              }
            }
            else {
              waterwayDesignStore.profileSettings = waterwayDesignStore.initialProfileSettings;
            }
          }
          else{
            await MessageCenter.addNotificationFromCode(responseData.errorCode!);
          }
            
        }
    }catch(err){
        console.error('getDesignUiState: error:', err)
    }
    finally{
      designStore.setLoader('')
      if (!designStore.hideSingleCrossSection && waterwayDesignStore.uiState !== "ReachDesignCalculate" && waterwayDesignStore.uiState !== "ReachDesignSurface") {
        designStore.structureLayerExists = false;
        await MessageCenter.addErrorFromCode('CS_3');
      }
    }
  }

  // Useeffect to populate reach table when the project is saved in waterwayDesignStore with API getAllReachByAlignedElementDesignParametersId
  @action.bound async getAllReachesByAlignedElementDesignParametersId() {
    if (waterwayDesignStore.alignedDesignElementId) {
        const response = await WaterwayDesignToolsReachJsonController.getAllReachByAlignedElementDesignParametersIdUsingGET(waterwayDesignStore.alignedDesignElementId);
        if (response && response.data) {
            waterwayDesignStore.waterwayDesignObject.waterwayDesignStations = [];               
            const waterwayVertexJsonList: string[] = this.sortWaterwayData(response.data).map(e => e.waterwayVertexJson!);
            waterwayVertexJsonList.forEach(waterwayVertexJso => {
                if (waterwayVertexJso) {
                    const waterwayVertex = JSON.parse(waterwayVertexJso);
                    waterwayDesignStore.waterwayDesignObject.waterwayDesignStations.push(
                        {
                            status: waterwayVertex.status ? waterwayVertex.status : "",
                            reachId: waterwayVertex.reachId,
                            startStation: waterwayVertex.startStation ? waterwayVertex.startStation : "",
                            endStation: waterwayVertex.endStation ? waterwayVertex.endStation : "",
                            station: waterwayVertex.endStation ? waterwayVertex.endStation : "",
                            groundZ: waterwayVertex.groundElev.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            channelZ: waterwayVertex.channelElev.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            cutFill: waterwayVertex.cutFill.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            stationLength: waterwayVertex.stationDist.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            grade: waterwayVertex.grade,
                            leftSS: waterwayVertex.channelParams ? toDecimalPrecision(waterwayVertex.channelParams.leftSlopeRatio, 0) : 0,
                            rightSS: waterwayVertex.channelParams ? toDecimalPrecision(waterwayVertex.channelParams.rightSlopeRatio, 0) : 0,
                            bottomWidth: waterwayVertex.channelParams ? toDecimalPrecision(waterwayVertex.channelParams.minBedWidth, 0) : 0,
                            offset1: waterwayVertex.channelParams ? toDecimalPrecision(waterwayVertex.channelParams.offset1, 0) : 0,
                            offset2: waterwayVertex.channelParams ? toDecimalPrecision(waterwayVertex.channelParams.offset2, 0) : 0,
                            offset3: waterwayVertex.channelParams ? toDecimalPrecision(waterwayVertex.channelParams.offset3, 0) : 0,
                            runoffQ: waterwayVertex.simFlowQ.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            addedQ: waterwayVertex.addedQ.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            totalQ: waterwayVertex.totalQ.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            flowDepthFB: waterwayVertex.simDepth.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            flowTopWidth: waterwayVertex.simWidth.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            stabVelocity: waterwayVertex.stabVelocity.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            capaVelocity: waterwayVertex.capaVelocity.toLocaleString("en", { maximumFractionDigits: 1, minimumFractionDigits: 1, useGrouping: false }),
                            soilStrength: waterwayVertex.soilStrength ? waterwayVertex.soilStrength : "",
                            vegCover: waterwayVertex.vegetalCover ? waterwayVertex.vegetalCover : "",
                            stability: waterwayVertex.stability ? waterwayVertex.stability : "",
                            capacity: waterwayVertex.capacity ? waterwayVertex.capacity : "",
                            channelParams: waterwayVertex.channelParams ?
                                {
                                    startSlopeDistance: waterwayVertex.channelParams.startSlopeDistance,
                                    bermWidth: toDecimalPrecision(waterwayVertex.channelParams.bermWidth, 0),
                                    leftSlopeRatio: toDecimalPrecision(waterwayVertex.channelParams.leftSlopeRatio, 0),
                                    dipDepth: waterwayVertex.channelParams.dipDepth,
                                    offset2: toDecimalPrecision(waterwayVertex.channelParams.offset2, 0),
                                    sideWidthLimit: waterwayVertex.channelParams.sideWidthLimit,
                                    offset1: toDecimalPrecision(waterwayVertex.channelParams.offset1, 0),
                                    backSlopeRatio: waterwayVertex.channelParams.backSlopeRatio,
                                    sideSlope: waterwayVertex.channelParams.sideSlope,
                                    freeBoard: waterwayVertex.channelParams.freeBoard,
                                    offset3: toDecimalPrecision(waterwayVertex.channelParams.offset3, 0),
                                    rightSlopeRatio: toDecimalPrecision(waterwayVertex.channelParams.rightSlopeRatio, 0),
                                    minBedWidth: toDecimalPrecision(waterwayVertex.channelParams.minBedWidth, 0),
                                    endSlopeDistance: waterwayVertex.channelParams.endSlopeDistance,
                                }
                                : {
                                    startSlopeDistance: 0,
                                    bermWidth: 0,
                                    leftSlopeRatio: 0,
                                    dipDepth: 0,
                                    offset2: 0,
                                    sideWidthLimit: 0,
                                    offset1: 0,
                                    backSlopeRatio: 0,
                                    sideSlope: 0,
                                    freeBoard: 0,
                                    offset3: 0,
                                    rightSlopeRatio: 0,
                                    minBedWidth: 0,
                                    endSlopeDistance: 0,
                                },
                            channelShapeType: waterwayVertex.channelShapeType ? waterwayVertex.channelShapeType : "",
                            error: waterwayVertex.errorCode ? waterwayVertex.errorCode : "",
                            depthEdited: waterwayVertex.depthEdited,
                            widthEdited: waterwayVertex.widthEdited,
                            simDepth: waterwayVertex.simDepth,
                            depth: waterwayVertex.depth,
                        }
                    )
                }
            })

            waterwayDesignStore.waterwayStationsEditingClone = JSON.parse(JSON.stringify(waterwayDesignStore.waterwayDesignObject.waterwayDesignStations));
            this.tableStore.checkSlopeDirection(waterwayDesignStore.waterwayDesignObject.waterwayDesignStations);
         }
            
    }    
}

  // button control on design page when state is 6(InputsDave) or 7(Save & Porceed)
  buttonsControlDesignPage(){
    if (waterwayDesignStore.waterwayDesignObject.waterwayDesignStations.length > 0) {
      waterwayDesignStore.waterwayDesignObject.disableCalculateButtonDesignPage = false;
      waterwayDesignStore.waterwayDesignObject.DisableSaveButtonDesignPage = true;
      waterwayDesignStore.waterwayDesignObject.hasWaterwayCalculations = false;
      if (waterwayDesignStore.defaultLiningMaterialChanged[0] === true) {
        waterwayDesignStore.waterwayDesignObject.DisableSaveButtonDesignPage = false;
      }
    }
    else {
      waterwayDesignStore.waterwayDesignObject.disableCalculateButtonDesignPage = true;
      waterwayDesignStore.waterwayDesignObject.DisableSaveButtonDesignPage = true;
      waterwayDesignStore.waterwayDesignObject.hasWaterwayCalculations = false;
    }
  }

  sortWaterwayData(data: any[]): any[] {
    // Extract 'START' and 'END' elements
    const startElement = data.find(item => item.reachId === 'START');
    const endElement = data.find(item => item.reachId === 'END');

    // Filter out 'START' and 'END' elements
    const filteredData = data.filter(item => item.reachId !== 'START' && item.reachId !== 'END');

    // Sort the remaining elements by the numerical part of 'reachId'
    filteredData.sort((a, b) => {
        const aId = parseInt(a.reachId.split('-')[1]);
        const bId = parseInt(b.reachId.split('-')[1]);
        return aId - bId;
    });

    // Re-add 'START' and 'END' elements at the beginning and end
    if (startElement) filteredData.unshift(startElement);
    if (endElement) filteredData.push(endElement);

    return filteredData;
}

  @observable notification: any = {
    unsavedEdits: '',
  }

  @action.bound async getErrors() {
    waterwayDesignStore.notifications.unsavedEdits = await MessageCenter.addErrorLabelFromCode('WDT_114');
    waterwayDesignStore.notifications.unsavedEditsClosePage = await MessageCenter.addErrorLabelFromCode('WDT_115');
  }

  // @action.bound async fetchLiningMaterial() {
  //   const alignedElementDesignParamsId = waterwayDesignStore.alignedDesignElementId;
  //   if (alignedElementDesignParamsId) {
  //     try {
  //       const response = await WaterwayDesignToolsController.getLiningMaterialByAlignedElementDesignParamsIdUsingGET(
  //         alignedElementDesignParamsId
  //       );
  
  //       let responseData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
  //       if (responseData && responseData.waterwayMaterial) {
  //         const newLiningMaterial = responseData.waterwayMaterial;          
  //         waterwayDesignStore.updateLiningMaterial(newLiningMaterial);
  //         if (this.waterwayInputVM) {
  //             this.waterwayInputVM.syncLiningMaterialWithStore();
  //         }
  //     }
  //     } catch (error) {
  //       MessageCenter.addError('Failed to fetch lining material.');
  //     }
  //   }
  // }

//   @action.bound async applyPendingLiningMaterialChange() {
//     if (waterwayDesignStore.pendingLiningMaterialChange) {
//         const editedWaterwayVertexData = new Map();
//         waterwayDesignStore.waterwayDesignObject.waterwayDesignStations
//             .filter(station => station.reachId !== 'START' && station.reachId !== 'END' && station.reachId !== 'R-0')
//             .forEach(station => {
//                 const vertexDTO: IWaterwayVertexParamDTO = convertStationBasedOnWaterwayVertexParamDTO(station, waterwayDesignStore.channelControl);
//                 editedWaterwayVertexData.set(station.reachId, vertexDTO);
//             });

//         waterwayDesignStore.pendingLiningMaterialChange = null;
//     }
// }

@action.bound changeTab = async (section) => {
  if (waterwayDesignStore.currentWorkflowStep !== section) {
    this.atemptSetWorkflowStep(section);
  }
};


}